import maplibregl from 'maplibre-gl';
import { Chart } from 'chart.js/auto';
import { getTileUrl } from '../../../../config';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class DarkSkiesAstroWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const data = ctx.data;
    const showDarkskiesChart = widget.options?.show_darkskies_chart ?? (ctx.activePersonaId === 'photography' || ctx.activePersonaId === 'stargazing');
    const showAuroraMap = widget.options?.show_aurora_map ?? true;

    return data.darkskiesTrends.map((trend: any, i: number) => {
      return `
        <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px; display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 12px; margin-bottom: 16px;">
          <div style="grid-column: 1 / -1; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 6px;">
            <h3 style="margin: 0; color: #f8fafc; font-size: 0.95rem;">Astronomical Events - ${trend.dateLabel} <span style="font-size: 0.7rem; color: #10b981; margin-left: 6px; font-weight: normal; border: 1px solid #10b981; padding: 1px 4px; border-radius: 4px;">Proprietary Blend</span></h3>
            <div style="font-size: 0.8rem; font-weight: bold; color: #10b981;">Darkskies Score: ${trend.darkskiesScore.toFixed(1)}/10</div>
          </div>
          
          <!-- Left Col: Moon & Astro Times -->
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <div style="display: flex; align-items: center; gap: 15px;">
              <span style="font-size: 2.3rem;">${trend.moonIcon}</span>
              <div>
                <div style="font-weight: 700; color: #fff; font-size: 0.85rem;">${trend.moonName}</div>
                <div style="font-size: 0.75rem; color: var(--text-muted);">${trend.moonIllumination}</div>
              </div>
            </div>
            <div class="astro-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 4px;">
              <div style="background: rgba(0, 0, 0, 0.2); padding: 6px 10px; border-radius: 6px; font-size: 0.75rem; color: var(--text-muted);">
                🌌 Astro Dusk
                <div style="font-size: 0.9rem; font-weight: 600; color: #fff; margin-top: 2px;">${trend.astroDuskTime}</div>
              </div>
              <div style="background: rgba(0, 0, 0, 0.2); padding: 6px 10px; border-radius: 6px; font-size: 0.75rem; color: var(--text-muted);">
                🌅 Astro Dawn
                <div style="font-size: 0.9rem; font-weight: 600; color: #fff; margin-top: 2px;">${trend.astroDawnTime}</div>
              </div>
              <div style="background: rgba(0, 0, 0, 0.2); padding: 6px 10px; border-radius: 6px; font-size: 0.75rem; color: var(--text-muted);">
                🌙 Moon Rise
                <div style="font-size: 0.9rem; font-weight: 600; color: #fff; margin-top: 2px;">${trend.moonRiseTime}</div>
              </div>
              <div style="background: rgba(0, 0, 0, 0.2); padding: 6px 10px; border-radius: 6px; font-size: 0.75rem; color: var(--text-muted);">
                🌜 Moon Set
                <div style="font-size: 0.9rem; font-weight: 600; color: #fff; margin-top: 2px;">${trend.moonSetTime}</div>
              </div>
            </div>
            
            ${i === 0 ? `
            <div style="margin-top: 10px; border-top: 1px solid var(--border-light); padding-top: 10px;">
              <h4 style="font-size: 0.75rem; font-weight: 600; text-transform: uppercase; color: var(--text-primary); margin: 0 0 6px 0;">7-Day Moon Phase Outlook</h4>
              <div style="display: flex; flex-direction: column; gap: 4px;">
                ${data.moonPhaseOutlook.map((m: any) => `
                  <div style="display: flex; justify-content: space-between; font-size: 0.72rem; color: var(--text-muted);">
                    <span>${m.date}: ${m.icon} ${m.name}</span>
                    <span style="color: #fff;">Rise: ${m.rise} | Set: ${m.set}</span>
                  </div>
                `).join('')}
              </div>
            </div>
            ` : ''}
          </div>

          <!-- Right Col: Dark Skies Hourly Chart & Aurora Map/Forecast -->
          <div style="display: flex; flex-direction: column; border-left: 1px solid rgba(255,255,255,0.05); padding-left: 12px;">
            ${showDarkskiesChart ? `
              <div style="font-size: 0.75rem; font-weight: 600; color: var(--text-muted); margin-bottom: 4px;">HOURLY DARK SKIES SCORE (SUNSET ➔ SUNRISE)</div>
              <div style="height: 105px; position: relative; margin-bottom: 12px;">
                <canvas id="poi-darkskies-chart-${i}"></canvas>
              </div>
            ` : ''}
            ${showAuroraMap ? `
              <div style="font-size: 0.75rem; font-weight: 600; color: var(--text-muted); margin-bottom: 4px;">AURORA DENSITY & KP FORECAST</div>
              ${trend.isAuroraAvailable ? `
                <div id="poi-aurora-map-${i}" style="height: 150px; width: 100%; border-radius: 4px; background: #0f172a; margin-bottom: 4px;"></div>
              ` : `
                <div style="font-size: 0.72rem; color: var(--text-muted); font-style: italic; margin-bottom: 4px; text-align: center; padding: 10px 0; background: rgba(0,0,0,0.1); border-radius: 4px;">Aurora viewline not available for this night</div>
              `}
              <div style="height: 90px; position: relative;">
                <canvas id="poi-aurora-chart-${i}"></canvas>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');
  }

  public init(_widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const data = ctx.data;
    const manifest = ctx.manifest;

    data.darkskiesTrends.forEach((trend: any, i: number) => {
      // 1. Darkskies Overnight Trend Chart
      const dsCanvas = document.getElementById(`poi-darkskies-chart-${i}`) as HTMLCanvasElement;
      if (dsCanvas && !data.hasBinaryPointData && !TierAccessService.userHasLayerAccess('darkskies')) {
        const parent = dsCanvas.parentElement;
        if (parent) {
          dsCanvas.style.display = 'none';
          parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
            denial: data.accessDenial || ForecastBinaryService.lastAccessDenial,
            requiredTier: 'pro',
            featureLabel: 'hourly dark skies stargazing trend charts',
            compact: true
          });
        }
      } else if (dsCanvas && trend.scores && trend.scores.length > 0) {
        const activeColor = '#06b6d4';
        const dsChart = new Chart(dsCanvas, {
          type: 'line',
          data: {
            labels: trend.labels,
            datasets: [{
              label: 'Dark Skies Score',
              data: trend.scores,
              borderColor: activeColor,
              backgroundColor: 'rgba(6, 182, 212, 0.15)',
              fill: true,
              tension: 0.3,
              pointRadius: 2,
              pointHoverRadius: 4
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `Dark Skies Score: ${(context.parsed.y ?? 0).toFixed(1)} / 10`
                }
              }
            },
            scales: {
              y: {
                min: 0,
                max: 10,
                ticks: { color: '#94a3b8', font: { size: 9 }, stepSize: 2 },
                grid: { color: 'rgba(255,255,255,0.05)' }
              },
              x: {
                ticks: { color: '#94a3b8', font: { size: 8 } },
                grid: { display: false }
              }
            }
          }
        });
        ctx.charts.push(dsChart);
      }

      // 2. Aurora Chart (Kp Index)
      const auroraCanvas = document.getElementById(`poi-aurora-chart-${i}`) as HTMLCanvasElement;
      if (auroraCanvas && trend.kpValues.length > 0) {
        const auroraChart = new Chart(auroraCanvas, {
          type: 'bar',
          data: {
            labels: trend.kpLabels,
            datasets: [{
              label: 'Kp Index',
              data: trend.kpValues,
              backgroundColor: trend.kpValues.map((kp: number) => {
                if (kp >= 5) return '#ef4444';
                if (kp >= 3.5) return '#f59e0b';
                return '#10b981';
              }),
              borderWidth: 0,
              borderRadius: 4
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              y: {
                min: 0,
                max: 9,
                ticks: { color: '#94a3b8', font: { size: 8 } },
                grid: { color: 'rgba(255,255,255,0.05)' }
              },
              x: {
                ticks: { color: '#94a3b8', font: { size: 8 } },
                grid: { display: false }
              }
            }
          }
        });
        ctx.charts.push(auroraChart);
      }

      // 3. Aurora Map
      const auroraMapContainer = document.getElementById(`poi-aurora-map-${i}`);
      if (manifest && auroraMapContainer && trend.isAuroraAvailable) {
        const auroraUrl = getTileUrl(manifest, 'aurora', '', trend.auroraDateStr, '');

        const mapInstance = new maplibregl.Map({
          container: `poi-aurora-map-${i}`,
          style: {
            version: 8,
            sources: {
              osm: {
                type: 'raster',
                tiles: ['https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'],
                tileSize: 256
              },
              aurora: {
                type: 'raster',
                url: auroraUrl.replace(/\/{z}\/{x}\/{y}.*$/, ''),
                tileSize: 256
              }
            },
            layers: [
              {
                id: 'osm-layer',
                type: 'raster',
                source: 'osm',
                minzoom: 0,
                maxzoom: 18
              },
              {
                id: 'aurora-layer',
                type: 'raster',
                source: 'aurora',
                paint: { 'raster-opacity': 0.7 }
              }
            ]
          },
          center: [ctx.lon, ctx.lat],
          zoom: 3,
          interactive: false,
          attributionControl: false
        });

        new maplibregl.Marker({ color: '#facc15' })
          .setLngLat([ctx.lon, ctx.lat])
          .addTo(mapInstance);

        ctx.maps.push(mapInstance);
      }
    });
  }
}
